import { supabase, handleSupabaseError, handleSupabaseSuccess } from '../lib/supabase';
import { ApiResponse, YouTubePlaylist, VideoContent } from '../types';

const YOUTUBE_API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY || '';
const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL || 'https://m7dxffi3e9.ap-southeast-1.awsapprunner.com';
const MAX_SHORT_DURATION_SECONDS = 180;

async function getAdminProfileId(): Promise<string> {
  const { data } = await supabase
    .from('profiles')
    .select('id')
    .eq('email', 'support+test123@learnova.info')
    .single();
  if (data?.id) return data.id;
  const { data: fallback } = await supabase
    .from('profiles')
    .select('id')
    .eq('role', 'teacher')
    .limit(1)
    .single();
  return fallback?.id || '00000000-0000-0000-0000-000000000000';
}

interface YouTubeSnippet {
  title: string;
  description: string;
  thumbnails: {
    default?: { url: string };
    medium?: { url: string };
    high?: { url: string };
  };
  resourceId?: { videoId: string };
  channelId: string;
  channelTitle: string;
}

interface YouTubePlaylistItemResponse {
  items: Array<{ snippet: YouTubeSnippet }>;
  nextPageToken?: string;
}

interface YouTubeVideoDetailResponse {
  items: Array<{
    id: string;
    contentDetails: { duration: string; caption: string };
    snippet: YouTubeSnippet;
  }>;
}

interface YouTubeChannelResponse {
  items: Array<{
    id: string;
    snippet: { title: string; customUrl?: string; thumbnails: { default?: { url: string } } };
  }>;
}

export interface ParsedVideo {
  youtubeVideoId: string;
  title: string;
  description: string;
  thumbnailUrl: string;
  durationSeconds: number;
  durationFormatted: string;
  hasCaptions: boolean;
  channelId: string;
  channelTitle: string;
  channelHandle?: string;
  channelIconUrl?: string;
  eligible: boolean;
  ineligibleReason?: string;
}

export interface PlaylistPreview {
  youtubePlaylistId: string;
  title: string;
  description: string;
  thumbnailUrl: string;
  videos: ParsedVideo[];
  eligibleCount: number;
  totalCount: number;
}

class YouTubeService {
  private static instance: YouTubeService;
  private constructor() {}

  public static getInstance(): YouTubeService {
    if (!YouTubeService.instance) {
      YouTubeService.instance = new YouTubeService();
    }
    return YouTubeService.instance;
  }

  extractPlaylistId(url: string): string | null {
    const patterns = [
      /[?&]list=([a-zA-Z0-9_-]+)/,
      /playlist\?list=([a-zA-Z0-9_-]+)/,
      /^([a-zA-Z0-9_-]+)$/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  }

  private parseDuration(iso8601: string): number {
    const match = iso8601.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!match) return 0;
    return parseInt(match[1] || '0', 10) * 3600 + parseInt(match[2] || '0', 10) * 60 + parseInt(match[3] || '0', 10);
  }

  private formatDuration(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  async fetchPlaylistPreview(playlistUrl: string): Promise<ApiResponse<PlaylistPreview>> {
    try {
      const playlistId = this.extractPlaylistId(playlistUrl);
      if (!playlistId) {
        return { success: false, data: null as any, error: 'Invalid YouTube playlist URL' };
      }

      const allItems: Array<{ snippet: YouTubeSnippet }> = [];
      let nextPageToken: string | undefined;

      do {
        const pageParam = nextPageToken ? `&pageToken=${nextPageToken}` : '';
        const itemsRes = await fetch(
          `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&playlistId=${playlistId}&maxResults=50&key=${YOUTUBE_API_KEY}${pageParam}`
        );
        if (!itemsRes.ok) {
          const err = await itemsRes.json();
          return { success: false, data: null as any, error: err.error?.message || 'Failed to fetch playlist' };
        }
        const itemsData: YouTubePlaylistItemResponse = await itemsRes.json();
        allItems.push(...itemsData.items);
        nextPageToken = itemsData.nextPageToken;
      } while (nextPageToken);

      if (allItems.length === 0) {
        return { success: false, data: null as any, error: 'Playlist is empty' };
      }

      const playlistTitle = allItems[0]?.snippet?.title || 'Untitled Playlist';
      const playlistThumb = allItems[0]?.snippet?.thumbnails?.high?.url || allItems[0]?.snippet?.thumbnails?.medium?.url || '';

      const videoIds = allItems.map(item => item.snippet.resourceId?.videoId).filter(Boolean) as string[];
      const videoDetails = new Map<string, { durationSeconds: number; hasCaptions: boolean; snippet: YouTubeSnippet }>();

      for (let i = 0; i < videoIds.length; i += 50) {
        const batch = videoIds.slice(i, i + 50);
        const detailRes = await fetch(
          `https://www.googleapis.com/youtube/v3/videos?part=contentDetails,snippet&id=${batch.join(',')}&key=${YOUTUBE_API_KEY}`
        );
        if (detailRes.ok) {
          const detailData: YouTubeVideoDetailResponse = await detailRes.json();
          for (const item of detailData.items) {
            videoDetails.set(item.id, {
              durationSeconds: this.parseDuration(item.contentDetails.duration),
              hasCaptions: item.contentDetails.caption === 'true',
              snippet: item.snippet,
            });
          }
        }
      }

      const channelIds = [...new Set(allItems.map(i => i.snippet.channelId).filter(Boolean))];
      const channelInfo = new Map<string, { handle?: string; iconUrl?: string }>();

      if (channelIds.length > 0) {
        const channelRes = await fetch(
          `https://www.googleapis.com/youtube/v3/channels?part=snippet&id=${channelIds.join(',')}&key=${YOUTUBE_API_KEY}`
        );
        if (channelRes.ok) {
          const channelData: YouTubeChannelResponse = await channelRes.json();
          for (const ch of channelData.items) {
            channelInfo.set(ch.id, { handle: ch.snippet.customUrl, iconUrl: ch.snippet.thumbnails?.default?.url });
          }
        }
      }

      const videos: ParsedVideo[] = videoIds.map(videoId => {
        const detail = videoDetails.get(videoId);
        const playlistItem = allItems.find(i => i.snippet.resourceId?.videoId === videoId);
        const snippet = detail?.snippet || playlistItem?.snippet;
        const chInfo = channelInfo.get(snippet?.channelId || '');
        const dur = detail?.durationSeconds ?? 0;
        let eligible = true;
        let ineligibleReason: string | undefined;
        if (dur > MAX_SHORT_DURATION_SECONDS) {
          eligible = false;
          ineligibleReason = `Duration exceeds ${MAX_SHORT_DURATION_SECONDS / 60} minutes`;
        }
        return {
          youtubeVideoId: videoId,
          title: snippet?.title || 'Untitled',
          description: snippet?.description || '',
          thumbnailUrl: snippet?.thumbnails?.high?.url || snippet?.thumbnails?.medium?.url || '',
          durationSeconds: dur,
          durationFormatted: this.formatDuration(dur),
          hasCaptions: detail?.hasCaptions ?? false,
          channelId: snippet?.channelId || '',
          channelTitle: snippet?.channelTitle || '',
          channelHandle: chInfo?.handle,
          channelIconUrl: chInfo?.iconUrl,
          eligible,
          ineligibleReason,
        };
      });

      return handleSupabaseSuccess<PlaylistPreview>({
        youtubePlaylistId: playlistId,
        title: playlistTitle,
        description: '',
        thumbnailUrl: playlistThumb,
        videos,
        eligibleCount: videos.filter(v => v.eligible).length,
        totalCount: videos.length,
      });
    } catch (error) {
      return { success: false, data: null as any, error: (error as Error).message || 'Failed to fetch playlist preview' };
    }
  }

  async insertPlaylist(preview: PlaylistPreview, category: string): Promise<ApiResponse<YouTubePlaylist>> {
    try {
      const adminId = await getAdminProfileId();
      const eligibleVideos = preview.videos.filter(v => v.eligible);
      const { data: playlist, error: playlistError } = await supabase
        .from('youtube_playlists')
        .insert({
          youtube_playlist_id: preview.youtubePlaylistId,
          title: preview.title,
          description: preview.description,
          thumbnail_url: preview.thumbnailUrl,
          teacher_id: adminId,
          video_count: eligibleVideos.length,
          videos_with_captions: eligibleVideos.filter(v => v.hasCaptions).length,
          videos_processed: 0,
          is_published: false,
        })
        .select()
        .single();

      if (playlistError) return handleSupabaseError<YouTubePlaylist>(playlistError);

      const videoInserts = eligibleVideos.map(v => ({
        title: v.title,
        description: v.description,
        video_url: `https://www.youtube.com/shorts/${v.youtubeVideoId}`,
        thumbnail_url: v.thumbnailUrl,
        duration_seconds: v.durationSeconds,
        creator_id: adminId,
        youtube_video_id: v.youtubeVideoId,
        youtube_playlist_id: playlist.id,
        source_type: 'youtube_shorts' as const,
        has_captions: v.hasCaptions,
        is_published: false,
        quiz_generated: false,
        vocabulary_generated: false,
        subtitles_generated: false,
        subject: category,
        youtube_channel_handle: v.channelHandle || null,
        youtube_channel_id: v.channelId || null,
        youtube_channel_icon_url: v.channelIconUrl || null,
      }));

      if (videoInserts.length > 0) {
        const { error: videoError } = await supabase.from('videos').insert(videoInserts);
        if (videoError) {
          await supabase.from('youtube_playlists').delete().eq('id', playlist.id);
          return handleSupabaseError<YouTubePlaylist>(videoError);
        }
      }

      return handleSupabaseSuccess(this.mapRowToPlaylist(playlist));
    } catch (error) {
      return handleSupabaseError<YouTubePlaylist>(error);
    }
  }

  async generateQuizAndVocabulary(
    videoId: string,
    youtubeVideoId: string,
    videoTitle: string,
    category: string,
    channelHandle?: string,
    channelIconUrl?: string
  ): Promise<ApiResponse<void>> {
    try {
      const body: any = {
        youtube_video_id: youtubeVideoId,
        video_title: videoTitle,
        category,
        language: 'en',
        generate_both_languages: true,
      };
      if (channelHandle) body.youtube_channel_handle = channelHandle;
      if (channelIconUrl) body.youtube_channel_icon_url = channelIconUrl;

      const res = await fetch(`${BACKEND_API_URL}/api/youtube/generate-quiz`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        return { success: false, data: undefined as any, error: errData.error || `API error: ${res.status}` };
      }

      // Backend saves quizzes, vocabulary, and subtitles directly to Supabase.
      // We just need to update the generation flags on the videos table.
      await res.json().catch(() => ({}));

      await supabase.from('videos').update({
        quiz_generated: true,
        vocabulary_generated: true,
        subtitles_generated: true,
      }).eq('id', videoId);

      await this.updatePlaylistProcessedCount(videoId);

      return handleSupabaseSuccess(undefined, 'Quiz and vocabulary generated successfully');
    } catch (error) {
      return handleSupabaseError<void>(error);
    }
  }

  private async updatePlaylistProcessedCount(videoId: string): Promise<void> {
    const { data: video } = await supabase.from('videos').select('youtube_playlist_id').eq('id', videoId).single();
    if (video?.youtube_playlist_id) {
      const { count } = await supabase.from('videos').select('*', { count: 'exact', head: true }).eq('youtube_playlist_id', video.youtube_playlist_id).eq('quiz_generated', true);
      await supabase.from('youtube_playlists').update({ videos_processed: count || 0 }).eq('id', video.youtube_playlist_id);
    }
  }

  async regenerateQuiz(videoId: string, youtubeVideoId: string, videoTitle: string, category: string, channelHandle?: string, channelIconUrl?: string): Promise<ApiResponse<void>> {
    try {
      await supabase.from('quizzes').delete().eq('video_id', videoId);
      await supabase.from('vocabulary_words').delete().eq('video_id', videoId);
      await supabase.from('videos').update({ quiz_generated: false, vocabulary_generated: false }).eq('id', videoId);
      return this.generateQuizAndVocabulary(videoId, youtubeVideoId, videoTitle, category, channelHandle, channelIconUrl);
    } catch (error) {
      return handleSupabaseError<void>(error);
    }
  }

  async getPlaylists(): Promise<ApiResponse<YouTubePlaylist[]>> {
    try {
      const { data, error } = await supabase.from('youtube_playlists').select('*').order('created_at', { ascending: false });
      if (error) return handleSupabaseError<YouTubePlaylist[]>(error);
      return handleSupabaseSuccess((data || []).map(row => this.mapRowToPlaylist(row)));
    } catch (error) {
      return handleSupabaseError<YouTubePlaylist[]>(error);
    }
  }

  async getPlaylistVideos(playlistId: string): Promise<ApiResponse<VideoContent[]>> {
    try {
      const { data, error } = await supabase.from('videos').select('*').eq('youtube_playlist_id', playlistId).order('created_at', { ascending: true });
      if (error) return handleSupabaseError<VideoContent[]>(error);
      return handleSupabaseSuccess((data || []).map(row => this.mapRowToVideo(row)));
    } catch (error) {
      return handleSupabaseError<VideoContent[]>(error);
    }
  }

  async publishPlaylist(playlistId: string): Promise<ApiResponse<void>> {
    try {
      const { error: ve } = await supabase.from('videos').update({ is_published: true }).eq('youtube_playlist_id', playlistId);
      if (ve) return handleSupabaseError<void>(ve);
      const { error: pe } = await supabase.from('youtube_playlists').update({ is_published: true, updated_at: new Date().toISOString() }).eq('id', playlistId);
      if (pe) return handleSupabaseError<void>(pe);
      return handleSupabaseSuccess(undefined, 'Playlist published successfully');
    } catch (error) {
      return handleSupabaseError<void>(error);
    }
  }

  async unpublishPlaylist(playlistId: string): Promise<ApiResponse<void>> {
    try {
      const { error: ve } = await supabase.from('videos').update({ is_published: false }).eq('youtube_playlist_id', playlistId);
      if (ve) return handleSupabaseError<void>(ve);
      const { error: pe } = await supabase.from('youtube_playlists').update({ is_published: false, updated_at: new Date().toISOString() }).eq('id', playlistId);
      if (pe) return handleSupabaseError<void>(pe);
      return handleSupabaseSuccess(undefined, 'Playlist unpublished successfully');
    } catch (error) {
      return handleSupabaseError<void>(error);
    }
  }

  async deletePlaylist(playlistId: string): Promise<ApiResponse<void>> {
    try {
      const { data: videos } = await supabase.from('videos').select('id').eq('youtube_playlist_id', playlistId);
      if (videos && videos.length > 0) {
        const videoIds = videos.map(v => v.id);
        await supabase.from('quizzes').delete().in('video_id', videoIds);
        await supabase.from('vocabulary_words').delete().in('video_id', videoIds);
        await supabase.from('videos').delete().in('id', videoIds);
      }
      const { error } = await supabase.from('youtube_playlists').delete().eq('id', playlistId);
      if (error) return handleSupabaseError<void>(error);
      return handleSupabaseSuccess(undefined, 'Playlist deleted successfully');
    } catch (error) {
      return handleSupabaseError<void>(error);
    }
  }

  async deleteVideo(videoId: string): Promise<ApiResponse<void>> {
    try {
      const { data: video } = await supabase.from('videos').select('youtube_playlist_id').eq('id', videoId).single();
      await supabase.from('quizzes').delete().eq('video_id', videoId);
      await supabase.from('vocabulary_words').delete().eq('video_id', videoId);
      const { error } = await supabase.from('videos').delete().eq('id', videoId);
      if (error) return handleSupabaseError<void>(error);
      if (video?.youtube_playlist_id) {
        const { count } = await supabase.from('videos').select('*', { count: 'exact', head: true }).eq('youtube_playlist_id', video.youtube_playlist_id);
        await supabase.from('youtube_playlists').update({ video_count: count || 0 }).eq('id', video.youtube_playlist_id);
      }
      return handleSupabaseSuccess(undefined, 'Video deleted successfully');
    } catch (error) {
      return handleSupabaseError<void>(error);
    }
  }

  private mapRowToPlaylist(row: any): YouTubePlaylist {
    return {
      id: row.id, youtubePlaylistId: row.youtube_playlist_id, title: row.title, description: row.description || '',
      thumbnailUrl: row.thumbnail_url || '', teacherId: row.teacher_id, videoCount: row.video_count || 0,
      videosWithCaptions: row.videos_with_captions || 0, videosProcessed: row.videos_processed || 0,
      isPublished: row.is_published || false, createdAt: new Date(row.created_at),
      updatedAt: row.updated_at ? new Date(row.updated_at) : undefined,
    };
  }

  private mapRowToVideo(row: any): VideoContent {
    return {
      id: row.id, title: row.title, description: row.description || '', creatorId: row.creator_id,
      videoUrl: row.video_url || '', thumbnailUrl: row.thumbnail_url || '', durationSeconds: row.duration_seconds || 0,
      subject: row.subject || '', isPublished: row.is_published || false, viewCount: row.view_count || 0,
      likeCount: row.like_count || 0, commentCount: row.comment_count || 0, youtubeVideoId: row.youtube_video_id,
      sourceType: row.source_type || 'youtube_shorts', hasCaptions: row.has_captions || false,
      youtubePlaylistId: row.youtube_playlist_id, youtubeChannelHandle: row.youtube_channel_handle,
      youtubeChannelId: row.youtube_channel_id, youtubeChannelIconUrl: row.youtube_channel_icon_url,
      quizGenerated: row.quiz_generated || false, vocabularyGenerated: row.vocabulary_generated || false,
      subtitlesGenerated: row.subtitles_generated || false, createdAt: new Date(row.created_at),
    };
  }
}

export const youtubeService = YouTubeService.getInstance();
